import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Crown, Gift, Trophy } from "lucide-react";
import { Link } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;

const RANK_COLORS = ["text-amber-400", "text-slate-300", "text-orange-400", "text-slate-500", "text-slate-500"];

export const Leaderboard = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/referrals/leaderboard`)
      .then((r) => r.json())
      .then(setData)
      .catch(() => {});
  }, []);

  if (!data || !data.leaders?.length) return null;
  const max = data.leaders[0].signups || 1;

  return (
    <section id="leaderboard" className="relative py-24 lg:py-32 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <span className="font-mono text-xs tracking-widest uppercase text-cyan-neon">[05] Referral leaderboard</span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white mt-4 leading-tight">
              Advertisers who share the shield.
            </h2>
            <p className="text-slate-400 mt-5 leading-relaxed font-light max-w-md">
              Every advertiser you refer earns you a free month of protection when they upgrade.
              These are this season&apos;s top defenders.
            </p>
            <Link
              data-testid="leaderboard-cta"
              to="/signup"
              className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-cyan-neon text-[#040914] text-sm font-semibold hover:glow-cyan transition-shadow"
            >
              <Gift className="w-4 h-4" /> Start free & get your invite link
            </Link>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            data-testid="referral-leaderboard"
            className="rounded-2xl neon-border bg-[#0B101A]/80 backdrop-blur-md p-7"
          >
            <div className="flex items-center gap-2.5 mb-6">
              <Trophy className="w-5 h-5 text-amber-400" />
              <span className="font-display text-white">Top referrers</span>
              <span className="ml-auto font-mono text-[10px] tracking-wider uppercase text-slate-500">free months earned</span>
            </div>
            <div className="space-y-4">
              {data.leaders.map((l, i) => (
                <motion.div
                  key={l.name + i}
                  initial={{ opacity: 0, x: -14 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  data-testid={`leaderboard-row-${i}`}
                  className="flex items-center gap-4"
                >
                  <span className={`w-6 font-display text-lg ${RANK_COLORS[i]}`}>
                    {i === 0 ? <Crown className="w-5 h-5" /> : `#${i + 1}`}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between text-sm mb-1.5">
                      <span className="text-white">{l.name}</span>
                      <span className="font-mono text-xs text-slate-400">
                        {l.signups} signup{l.signups !== 1 ? "s" : ""} · <span className="text-[#39FF14]">{l.converted}</span>
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-cyan-neon to-[#39FF14]"
                        style={{ width: `${(l.signups / max) * 100}%` }}
                      />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
